import React, {
	useMemo,
	useRef,
	useState,
	useEffect,
	useImperativeHandle,
	forwardRef,
	useCallback,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
	AllCommunityModule,
	ModuleRegistry,
	provideGlobalGridOptions,
} from "ag-grid-community";
import { useTranslation } from "react-i18next";
import "./ag-grid.scss";
import AG_GRID_LOCALE_EN from "./locale.en";
import AG_GRID_LOCALE_DE from "./locale.de";
import {
	iconSortUnSort,
	iconSortAscending,
	iconSortDescending,
} from "../../../assets/images/ag-grid";
ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

function AgGridAtom(props, ref) {
	const gridRef = useRef();
	const { t, i18n } = useTranslation();
	const {
		columnDefs,
		onRowSelection = () => { },
		gridHeight = "100%",
		updateRowOrder = () => { },
		context = {},
		showTotalRecords = true,
		onGridReady: propsOnGridReady,
		...rest
	} = props;
	const { rowData } = props;

	// Using useRef for values that shouldn't trigger re-renders
	const gridStateRef = useRef({
		totalRows: 0,
		filteredRows: 0,
		isFiltered: false
	});

	// Only use state for values that need to trigger UI updates
	const [gridStats, setGridStats] = useState({
		totalRows: 0,
		filteredRows: 0,
		isFiltered: false
	});

	// Get current language and memoize locale text
	const currentLanguage = i18n.language || localStorage.getItem("i18nextLng") || "en";
	const localeText = useMemo(() =>
		currentLanguage === "de" ? AG_GRID_LOCALE_DE : AG_GRID_LOCALE_EN,
		[currentLanguage]);

	// Memoize grid style to prevent container resizing issues
	const gridStyle = useMemo(() => ({
		height: gridHeight,
		width: "100%",
		overflow: "hidden", // Prevent scroll-induced reflows
	}), [gridHeight]);

	// Memoize default column definition
	const defaultColDef = useMemo(() => ({
		sortable: true,
		flex: 1,
		minWidth: 100,
		resizable: true,
	}), []);

	// Memoize grid icons to prevent recreation
	const gridIcons = useMemo(() => ({
		sortAscending: `<img src="${iconSortAscending}" style="height: 16px; width: 16px;"/>`,
		sortDescending: `<img src="${iconSortDescending}" style="height: 16px; width: 16px;"/>`,
		sortUnSort: `<img src="${iconSortUnSort}" style="height: 16px; width: 16px;"/>`,
	}), []);

	// Debounced update of grid stats to prevent rapid UI updates
	const updateGridStats = useCallback(() => {
		// Only update state if values have actually changed
		if (gridStateRef.current.totalRows !== gridStats.totalRows ||
			gridStateRef.current.filteredRows !== gridStats.filteredRows ||
			gridStateRef.current.isFiltered !== gridStats.isFiltered) {

			setGridStats({
				totalRows: gridStateRef.current.totalRows,
				filteredRows: gridStateRef.current.filteredRows,
				isFiltered: gridStateRef.current.isFiltered
			});
		}
	}, [gridStats]);

	// Use debounced effect for rowData changes
	useEffect(() => {
		if (rowData) {
			gridStateRef.current.totalRows = rowData.length;
			gridStateRef.current.filteredRows = rowData.length;
			gridStateRef.current.isFiltered = false;

			// Use requestAnimationFrame to batch UI updates
			requestAnimationFrame(updateGridStats);
		}
	}, [rowData, updateGridStats]);

	// Expose API methods via ref
	useImperativeHandle(ref, () => ({
		filterData: (filterText) => {
			if (gridRef.current && gridRef.current.api) {
				gridRef.current.api.setQuickFilter(filterText);
			}
		},
		getApi: () => {
			return gridRef.current?.api;
		},
	}), []);

	// Handle row selection changes
	const onSelectionChanged = useCallback(() => {
		if (!gridRef.current || !gridRef.current.api) return;
		const selectedRows = gridRef.current.api.getSelectedRows();
		onRowSelection(selectedRows);
	}, [onRowSelection]);

	// Handle row drag end for reordering
	const onRowDragEnd = useCallback(() => {
		if (!gridRef.current || !gridRef.current.api) return;

		const itemsToUpdate = [];
		gridRef.current.api.forEachNodeAfterFilterAndSort((rowNode, index) => {
			const data = rowNode.data;
			data.position = index + 1;
			itemsToUpdate.push(data);
		});

		updateRowOrder(itemsToUpdate);
	}, [updateRowOrder]);

	// Handle filter changes
	const onFilterChanged = useCallback(() => {
		if (!gridRef.current || !gridRef.current.api) return;

		const displayedRowCount = gridRef.current.api.getDisplayedRowCount();
		gridStateRef.current.filteredRows = displayedRowCount;
		gridStateRef.current.isFiltered = displayedRowCount !== gridStateRef.current.totalRows;

		// Debounce UI updates using requestAnimationFrame
		requestAnimationFrame(updateGridStats);
	}, [updateGridStats]);

	// Handle grid initialization
	const onGridReady = useCallback((params) => {
		if (propsOnGridReady) {
			propsOnGridReady(params);
		}

		const displayedRowCount = params.api.getDisplayedRowCount();
		gridStateRef.current.totalRows = displayedRowCount;
		gridStateRef.current.filteredRows = displayedRowCount;

		// Use setTimeout to ensure the grid has fully rendered before updating stats
		setTimeout(updateGridStats, 0);

		// Handle grid resize to prevent unnecessary reflows
		params.api.sizeColumnsToFit();
	}, [propsOnGridReady, updateGridStats]);

	return (
		<>
			<div className="ag-grid-container ag-theme-alpine" style={gridStyle}>
				<AgGridReact
					ref={gridRef}
					columnDefs={columnDefs}
					rowData={rowData}
					defaultColDef={defaultColDef}
					suppressRowHoverHighlight={false}
					suppressCellFocus={true}
					onSelectionChanged={onSelectionChanged}
					localeText={localeText}
					tooltipShowDelay={0}
					stopEditingWhenCellsLoseFocus={true}
					onRowDragEnd={onRowDragEnd}
					onFilterChanged={onFilterChanged}
					onGridReady={onGridReady}
					context={context}
					icons={gridIcons}
					domLayout="normal"
					suppressRowVirtualisation={false}
					{...rest}
				/>
			</div>

			{showTotalRecords && rowData && rowData.length > 0 && (
				<div className="ag-grid-total-records">
					{gridStats.isFiltered ? (
						<span>
							<strong className="record-count">{gridStats.filteredRows}</strong> {t('grid.of')} <strong className="record-count">{gridStats.totalRows}</strong> {t('grid.total-records')}
						</span>
					) : (
						<span>
							<strong className="record-count">{gridStats.totalRows}</strong> {t('grid.total-records')}
						</span>
					)}
				</div>
			)}
		</>
	);
}

export default forwardRef(AgGridAtom);
